/* eslint-disable jira/import-whitelist */
import { loadQuery, type PreloadedQuery } from 'react-relay';
import { checkAuthentication } from '@jira/spa-apps__interceptors/src/checkAuthentication.tsx';
import { isDevopsFeatureDisabledInFedRamp } from '@jira/software__devops-fedramp-utils/src/isDevopsFeatureDisabledInFedRamp.tsx';
import { createAri } from '@jira/platform__utils-ari/src/index.tsx';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { RELAY_RESOURCE_TYPE } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { toVersionIssuesGraphQLVariables } from '@jira/releases__issue-list-query-param-controller/src/controllers/graphql-variables/index.tsx';
import uiVersionDetailsQuery from '@jira/releases__version-detail/src/ui/__generated__/uiVersionDetailsQuery$parameters';
import type {
	uiVersionDetailsQuery$variables,
	uiVersionDetailsQuery as uiVersionDetailsQueryType,
} from '@jira/releases__version-detail/src/ui/__generated__/uiVersionDetailsQuery.graphql';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { expValEquals } from '@atlassian/jira-feature-experiments';

const RESOURCE_TYPE_PROJECTS_VERSION_DETAILS = 'PROJECTS_VERSION_DETAILS';

// Only used when isVersionDetailsCustomRouteEnabled on
export const versionDetailsRelayResourceCustom: RouteResource<
	PreloadedQuery<uiVersionDetailsQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<uiVersionDetailsQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_PROJECTS_VERSION_DETAILS}`,
	// Deliberately returning the static key, not to rerun query by resource, as different key returns null queryReference shortly while the query is running, when filter query parameter is changed,
	// which is believed to be a bug in react-resource-router part, but we don't know the reason yet.
	// Context: https://atlassian.slack.com/archives/CFLN3FEF8/p1680495188086669
	// Due to the same bug, issue navigator team is doing the similar technique: src/packages/platform/routes/src/ui/spa/issue-navigator/resources/index.tsx
	getKey: ({ match }) => {
		const { versionId } = match.params;
		const parsedVersionId = String(versionId ?? '').replace(/\D/g, '');

		return `jsw-releases-version-details-main-query-relay-${parsedVersionId}`;
	},
	getData: async ({ match, query }, { tenantContext }) => {
		const { versionId, projectKey } = match.params;
		const { filters, sortBy } = toVersionIssuesGraphQLVariables(query);
		const parsedVersionId = String(versionId ?? '').replace(/\D/g, '');

		const queryReference = loadQuery<uiVersionDetailsQueryType>(
			getRelayEnvironment(),
			uiVersionDetailsQuery,
			{
				cloudAri: createAri({
					resourceOwner: 'jira',
					resourceType: 'site',
					resourceId: tenantContext.cloudId,
				}),
				versionAri: createAri({
					resourceOwner: 'jira',
					cloudId: tenantContext.cloudId,
					resourceType: 'version/activation',
					resourceId: String(`${tenantContext.activationId}/${parsedVersionId}`),
				}),
				sortBy,
				filters,
				skip: !checkAuthentication(tenantContext) || parsedVersionId.length === 0,
				isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
				shouldIncludeDevOpsFieldsViaAgs: !isDevopsFeatureDisabledInFedRamp(),
				cloudId: tenantContext.cloudId,
				projectKey: projectKey || '',
				includeChangeHistory: expValEquals('release_change_history_enabled', 'isEnabled', true),
			} satisfies uiVersionDetailsQuery$variables,
			{
				fetchPolicy: 'store-or-network',
			},
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});
